import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';

import useExcelData from 'hooks/useExcelData';
import ExcelView from 'components/spreadsheet/ExcelView';

import { REJECT_TYPE_OPTIONS } from 'constants/grn';

const GrnLineAddModal = ({ show, onHide, onSave }) => {
  const [isSaving, setIsSaving] = useState(false);

  const {
    data,
    handleCellsChanged,
    addRow,
    getNewRows,
    resetData,
    getSelectedRows,
    deleteSelectedRows,
  } = useExcelData([]);

  useEffect(() => {
    if (show) {
      resetData([]);
    }
  }, [show, resetData]);

  // 행 추가
  const handleAddRow = useCallback(() => {
    addRow({
      is_selected: false,
      vendor_sku: '',
      received_category_a: 0,
      received_category_b: 0,
      received_category_c: 0,
      received_category_reject: 0,
      reject_type: '',
      reject_reason: '',
      expiration_date: '',
    });
  }, [addRow]);

  const handleDeleteSelected = useCallback(() => {
    deleteSelectedRows();
  }, [deleteSelectedRows]);

  const columns = [
    {
      key: 'is_selected',
      header: '',
      type: 'checkbox',
      width: 60,
    },
    { key: 'vendor_sku', header: 'Vendor SKU' },
    {
      key: 'received_category_a',
      header: 'Received (Category A)',
      type: 'number',
    },
    {
      key: 'received_category_b',
      header: 'Received (Category B)',
      type: 'number',
    },
    {
      key: 'received_category_c',
      header: 'Received (Category C)',
      type: 'number',
    },
    { key: 'received_category_reject', header: 'Rejected', type: 'number' },
    {
      key: 'reject_type',
      header: 'Rejected Type',
      type: 'dropdown',
      options: REJECT_TYPE_OPTIONS,
    },
    {
      key: 'reject_reason',
      header: 'Rejected Detail',
    },
    {
      key: 'expiration_date',
      header: 'Inventory Expiry Date',
      type: 'date',
    },
  ];

  const handleSave = async () => {
    const newRows = getNewRows();

    if (newRows.length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      const requestData = newRows.map(row => ({
        vendor_sku: row.vendor_sku,
        categories: [
          {
            inventory_type: 'a',
            quantity: Number(row.received_category_a) || 0,
          },
          {
            inventory_type: 'b',
            quantity: Number(row.received_category_b) || 0,
          },
          {
            inventory_type: 'c',
            quantity: Number(row.received_category_c) || 0,
          },
          {
            inventory_type: 'reject',
            quantity: Number(row.received_category_reject) || 0,
          },
        ],
        reject_type: row.reject_type || undefined,
        reject_reason: row.reject_reason || undefined,
        expiration_date: row.expiration_date || undefined,
      }));

      await onSave(requestData);
      onHide();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} fullscreen={true}>
      <Modal.Header closeButton>
        <Modal.Title>Add GRN Lines</Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-0">
        <div className="d-flex justify-content-end gap-2 mt-3 mb-3">
          {getSelectedRows().length > 0 && (
            <Button
              variant="falcon-default"
              size="sm"
              onClick={handleDeleteSelected}
              className="text-danger"
            >
              Delete Selected
            </Button>
          )}

          <Button variant="primary" size="sm" onClick={handleAddRow}>
            Add Row
          </Button>
        </div>

        {data?.length === 0 ? (
          <div className="text-center py-4">
            <p>No rows. Click 'Add Row' to start adding GRN lines.</p>
          </div>
        ) : (
          <ExcelView
            data={data}
            columns={columns}
            onCellsChanged={handleCellsChanged}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isSaving || getNewRows().length === 0}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GrnLineAddModal;
